import { useEffect, type ReactNode } from 'react';
import { motion } from 'framer-motion';

interface ModalRootProps {
  /** Click-on-backdrop close handler. */
  onClose: () => void;
  /** Stacking layer (`Z_MODALS`, `Z_DIALOGS_HIGH`, etc.). */
  zIndex: number;
  /** Animation duration override (ms or framer seconds). */
  durationSec?: number;
  /** Dialog content. Any clicks here NEVER bubble to backdrop. */
  children: ReactNode;
  /** Additional className applied to the inner motion.div content wrapper. */
  contentClass?: string;
  /**
   * Phase 12.1.1 — Disable the global ESC-to-close handler. Default: enabled.
   * Set this when a parent already handles ESC itself.
   */
  disableEscClose?: boolean;
}

/**
 * Phase 8.1 — Bulletproof modal root.
 *
 * Three problems this solves:
 *   1. Backdrop catching clicks intended for dialog content (pointer-events-none).
 *   2. Inconsistent click-to-close behaviour (mousedown on root with target check).
 *   3. z-index magic numbers scattered through className strings.
 *
 * Phase 12.1.1 (Bug 2/3) — Centralised ESC-close + flex centering verified:
 * the wrapper uses `inset-0 flex items-center justify-center` and the inner
 * `motion.div` has no transform that could push it off-screen. Tall content
 * is constrained via `max-h-[…vh] overflow-y-auto` on the contentClass.
 */
export function ModalRoot({
  onClose,
  zIndex,
  durationSec = 0.18,
  children,
  contentClass,
  disableEscClose,
}: ModalRootProps) {
  // Phase 12.1.1 — ESC closes the topmost modal.
  useEffect(() => {
    if (disableEscClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, disableEscClose]);

  return (
    <motion.div
      className="pointer-events-auto fixed inset-0 flex items-center justify-center overflow-y-auto px-4 py-6"
      style={{ zIndex }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: durationSec * 0.85 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop — visual only. pointer-events-none so clicks fall through to
          the wrapper's onMouseDown target check. */}
      <div
        className="pointer-events-none fixed inset-0 bg-bg-overlay backdrop-blur-sm"
        aria-hidden
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        className={[
          'pointer-events-auto relative my-auto shadow-lg',
          contentClass ?? '',
        ].join(' ')}
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.98 }}
        transition={{ duration: durationSec, ease: 'easeOut' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
